// ============================================
// COMPONENT - Tab Permisos (propietario asigna permisos de caja por vendedor)
// ============================================

import { useState } from 'react';
import { Skeleton } from '@almacenes/ui';
import {
  useVendedoresCaja,
  useCatalogoPermisos,
  usePermisosDeUsuario,
  useAsignarPermiso,
  useRevocarPermiso,
  useUIStore,
  type PermisoCaja,
  type AlcancePermiso,
} from '@almacenes/core';
import { ToggleSwitch } from './ToggleSwitch';

const ALCANCE_INFO: Record<AlcancePermiso, { titulo: string; descripcion: string }> = {
  PROPIO: {
    titulo: 'Sobre su propia caja',
    descripcion: 'Le da autonomía sobre su propio turno, sin depender del propietario.',
  },
  ALMACEN: {
    titulo: 'Sobre otros vendedores y el almacén',
    descripcion: 'Le da control sobre cajas, movimientos o configuración de todo el almacén.',
  },
};

export const TabPermisos = () => {
  const addToast = useUIStore((state) => state.addToast);
  const { data: vendedores, isLoading: isLoadingVendedores } = useVendedoresCaja();
  const { data: catalogo, isLoading: isLoadingCatalogo } = useCatalogoPermisos('CAJA');
  const [vendedorId, setVendedorId] = useState<string>('');

  const { data: permisosDelVendedor, isLoading: isLoadingPermisos } = usePermisosDeUsuario(
    vendedorId ? Number(vendedorId) : undefined
  );
  const asignar = useAsignarPermiso();
  const revocar = useRevocarPermiso();

  const handleToggle = (permiso: PermisoCaja, activar: boolean) => {
    if (!vendedorId) return;
    const usuarioId = Number(vendedorId);
    const mutation = activar ? asignar : revocar;
    mutation.mutate(
      { usuarioId, permiso },
      {
        onError: (err: any) => addToast('error', err?.message || 'Error al actualizar el permiso'),
      }
    );
  };

  const grupos = (['PROPIO', 'ALMACEN'] as const)
    .map((alcance) => ({ alcance, items: catalogo?.filter((c) => c.alcance === alcance) ?? [] }))
    .filter((grupo) => grupo.items.length > 0);

  return (
    <div className="flex flex-col md:flex-row gap-4 max-w-4xl">
      <div className="md:w-64 shrink-0 bg-white rounded-xl border border-gray-200 p-2">
        <p className="text-sm text-gray-500 px-3 py-2">Vendedores</p>
        {isLoadingVendedores ? (
          <div className="space-y-2 p-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <div className="max-h-[28rem] overflow-y-auto space-y-1">
            {vendedores?.map((v) => (
              <button
                key={v.id}
                onClick={() => setVendedorId(String(v.id))}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  vendedorId === String(v.id)
                    ? 'text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={vendedorId === String(v.id) ? { backgroundColor: '#11194F' } : undefined}
              >
                {v.nombreMostrable}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 bg-white rounded-xl border border-gray-200 p-6 min-w-0">
        {!vendedorId ? (
          <p className="text-center py-8 text-gray-500">Elige un vendedor de la lista para ver y ajustar sus permisos.</p>
        ) : isLoadingCatalogo || isLoadingPermisos ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {grupos.map((grupo) => (
              <div key={grupo.alcance}>
                <h3 className="font-semibold" style={{ color: '#11194F' }}>
                  {ALCANCE_INFO[grupo.alcance].titulo}
                </h3>
                <p className="text-sm text-gray-500 mb-1">{ALCANCE_INFO[grupo.alcance].descripcion}</p>
                <div className="divide-y divide-gray-200">
                  {grupo.items.map((item) => (
                    <ToggleSwitch
                      key={item.permiso}
                      label={item.etiqueta}
                      descripcion={item.descripcion}
                      checked={permisosDelVendedor?.includes(item.permiso) ?? false}
                      onChange={(activar) => handleToggle(item.permiso, activar)}
                      disabled={asignar.isPending || revocar.isPending}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
